import { useEffect, useMemo, useSyncExternalStore } from "react";
import { usePathname } from "../../navigation/use-pathname";
import { useSearchParams } from "../../navigation/use-search-params";
import { useFeatureNavigation } from "../../navigation/use-feature-navigation";
import { useAutomaticNavigationAllowed } from "../../navigation/activity/use-automatic-navigation-allowed";
import { matchRoute } from "@softmax/web-addresses";
import {
  feedbackQueryParam,
  buildFeedbackHref,
  buildFeedbackPageRoute,
} from "../page-route";
import {
  subscribeFeedbackSession,
  storageGet,
  storageSet,
  feedbackSessionStorageKey,
} from "./active-feedback-session";
export function useFeedbackSession() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const navigation = useFeatureNavigation();
  const search = searchParams.toString();
  const sessionIdFromUrl = searchParams.get(feedbackQueryParam);
  const storedSessionId = useSyncExternalStore(
    subscribeFeedbackSession,
    () => storageGet(feedbackSessionStorageKey),
    () => null,
  );
  const automaticNavigationAllowed = useAutomaticNavigationAllowed();
  const route = matchRoute(pathname);
  const isDisabledPath =
    route.name === "signIn" || route.name === "completeProfile";
  const activeSessionId = isDisabledPath
    ? null
    : (sessionIdFromUrl ?? storedSessionId);
  const pageRoute = useMemo(
    () => buildFeedbackPageRoute(pathname + (search ? "?" + search : "")),
    [pathname, search],
  );
  useEffect(() => {
    if (isDisabledPath) return;

    if (sessionIdFromUrl) {
      storageSet({
        key: feedbackSessionStorageKey,
        value: sessionIdFromUrl,
      });
      return;
    }

    if (!storedSessionId || !automaticNavigationAllowed) return;

    const currentHref = pathname + (search ? "?" + search : "");
    const restoredHref = buildFeedbackHref({
      route: currentHref,
      sessionId: storedSessionId,
    });

    if (restoredHref !== currentHref)
      navigation.replace({ href: restoredHref, scroll: false });
  }, [
    isDisabledPath,
    pathname,
    search,
    sessionIdFromUrl,
    navigation,
    storedSessionId,
    automaticNavigationAllowed,
  ]);
  return { pathname, search, activeSessionId, pageRoute, isDisabledPath };
}
